/**
 * CitedText - rendert Fliesstext mit Quellenverweisen wie [1], [2,3] oder [1-3]
 * und macht jede Nummer zu einem klickbaren Button. Ohne onCite bleibt der
 * Text unveraendert.
 */
import { Fragment, type ReactElement, type ReactNode } from "react";

const CITATION = /\[(\d+(?:\s*(?:,|-|–)\s*\d+)*)\]/g;

/** "1-3" -> [1,2,3], "2,3" -> [2,3] */
function expand(token: string): number[] {
  const out: number[] = [];
  for (const part of token.split(",")) {
    const range = part.trim().split(/-|–/);
    const lo = Number(range[0]);
    const hi = Number(range[range.length - 1]);
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi < lo || hi - lo > 50) continue;
    for (let n = lo; n <= hi; n++) out.push(n);
  }
  return out;
}

export function CitedText({
  text,
  onCite,
  isKnown,
  label,
}: {
  text: string | null | undefined;
  onCite?: (n: number) => void;
  /** Numbers without a source entry stay plain text instead of a dead button. */
  isKnown?: (n: number) => boolean;
  /** Accessible label for one citation button, e.g. n => `Quelle ${n} anzeigen` */
  label?: (n: number) => string;
}): ReactElement | null {
  if (!text) return null;
  if (!onCite) return <>{text}</>;

  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(CITATION)) {
    const start = m.index ?? 0;
    if (start > last) nodes.push(text.slice(last, start));
    const raw = m[1];
    const isRange = /-|–/.test(raw) && !raw.includes(",");
    const nums = expand(raw);
    if (!nums.length) {
      nodes.push(m[0]);
    } else {
      // A range like [1-3] stays one button pointing at its first source.
      const shown = isRange
        ? [{ text: raw, n: nums[0] }]
        : nums.map((n) => ({ text: String(n), n }));
      nodes.push(
        <Fragment key={`c-${key++}`}>
          [
          {shown.map((s, i) => (
            <Fragment key={i}>
              {i > 0 ? "," : null}
              {isKnown && !isKnown(s.n) ? (
                s.text
              ) : (
                <button
                  type="button"
                  onClick={() => onCite(s.n)}
                  aria-label={label ? label(s.n) : `Quelle ${s.n}`}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "0 1px",
                    margin: 0,
                    font: "inherit",
                    color: "var(--accent-cyan, #67e8f9)",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  {s.text}
                </button>
              )}
            </Fragment>
          ))}
          ]
        </Fragment>,
      );
    }
    last = start + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return <>{nodes}</>;
}
